import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import styles from "./PageNotFound.module.css";

const HOME_PATH = "/Home";
// The legacy 404.htm used <meta http-equiv="REFRESH" content="3; url=home.aspx" />.
const REDIRECT_SECONDS = 30;

export const PageNotFound = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [secondsLeft, setSecondsLeft] = useState(REDIRECT_SECONDS);

  useEffect(() => {
    // No other page manages document.title, so restore it when we leave.
    const previousTitle = document.title;
    document.title = "404 Error";
    return () => {
      document.title = previousTitle;
    };
  }, []);

  useEffect(() => {
    const timer = setInterval(() => {
      setSecondsLeft((prev) => (prev <= 1 ? 0 : prev - 1));
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    // replace: true so the browser Back button skips the dead URL.
    if (secondsLeft === 0) navigate(HOME_PATH, { replace: true });
  }, [secondsLeft, navigate]);

  return (
    <section className={styles.nfWrap}>
      <div className={styles.nfCard}>
        <svg className={styles.nfArt} viewBox="0 0 280 160" role="img" aria-label="Page not found">
          {/* cable coming in from the left, ending in a plug */}
          <path className={styles.nfArtCable} d="M4 80 C 40 80, 56 80, 78 80" />
          <rect className={styles.nfArtBody} x="78" y="58" width="42" height="44" rx="8" />
          <rect className={styles.nfArtPin} x="120" y="66" width="16" height="7" rx="3.5" />
          <rect className={styles.nfArtPin} x="120" y="87" width="16" height="7" rx="3.5" />

          {/* socket on the right, cable running off screen */}
          <path className={styles.nfArtCable} d="M276 80 C 240 80, 224 80, 202 80" />
          <rect className={styles.nfArtBody} x="160" y="58" width="42" height="44" rx="8" />
          <rect className={styles.nfArtPin} x="146" y="66" width="14" height="7" rx="3.5" />
          <rect className={styles.nfArtPin} x="146" y="87" width="14" height="7" rx="3.5" />

          {/* sparks across the gap */}
          <path className={styles.nfArtSpark} d="M137 44 l6 -13" />
          <path className={styles.nfArtSpark} d="M147 40 l-2 -14" />
          <path className={styles.nfArtSpark} d="M137 116 l6 13" />
        </svg>

        <div className={styles.nfCode}>404</div>
        <h3 className={styles.nfTitle}>Welcome to WES</h3>

        <p className={styles.nfText}>
          The page you are looking for has been moved, please go back to the{" "}
          <Link to={HOME_PATH} className={styles.nfLink}>
            Home Page
          </Link>
          .
        </p>

        {location?.pathname ? (
          <p className={styles.nfText}>
            <span className={styles.nfPath}>{location.pathname}</span>
          </p>
        ) : null}

        <p className={styles.nfCountdown}>
          {secondsLeft > 0 ? (
            <>
              Redirecting to the Home Page in <strong>{secondsLeft}</strong>{" "}
              {secondsLeft === 1 ? "second" : "seconds"}&hellip;
            </>
          ) : (
            <>Redirecting&hellip;</>
          )}
        </p>

        <div className={styles.nfActions}>
          <Link to={HOME_PATH} replace className={`${styles.nfBtn} ${styles.nfBtnPrimary}`}>
            Go to Home
          </Link>
          <button type="button" className={`${styles.nfBtn} ${styles.nfBtnSecondary}`} onClick={() => navigate(-1)}>
            Go Back
          </button>
        </div>

        <p className={styles.nfFooter}>
          Web :{" "}
                  <a href="https://wes.com.au/" className={styles.nfLink} target="_blank" rel="noreferrer">
            wes.com.au
          </a>
        </p>
      </div>
    </section>
  );
};

export default PageNotFound;
